import React, { useState, useEffect, useRef } from 'react';
import { PanelLeftClose, PanelLeftOpen, RefreshCw } from 'lucide-react';

interface PageInfo {
  page: number;
  label: string;
  sheetName?: string;
}

interface PageIndexSidebarProps {
  numPages: number | null;
  pageNumber: number;
  pageIndex?: PageInfo[] | null;
  pdfDocument: any;
  onNavigateToPage: (page: number) => void;
  onReindex?: () => Promise<void>;
}

const BADGE_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  power: { bg: 'var(--green-tint-medium)', color: 'var(--green-text)', border: 'var(--green-tint-strong)' },
  lighting: { bg: 'var(--yellow-tint-medium)', color: 'var(--yellow-text)', border: 'var(--yellow-tint-strong)' },
  demolition: { bg: 'var(--red-tint-medium)', color: 'var(--red-text)', border: 'var(--red-tint-strong)' },
  demo: { bg: 'var(--red-tint-medium)', color: 'var(--red-text)', border: 'var(--red-tint-strong)' },
  'fire alarm': { bg: 'var(--yellow-tint-medium)', color: 'var(--yellow-text-dark)', border: 'var(--yellow-tint-strong)' },
  'low voltage': { bg: 'var(--purple-tint-medium)', color: 'var(--purple-text)', border: 'var(--purple-tint-strong)' },
  telecom: { bg: 'var(--purple-tint-medium)', color: 'var(--purple-text)', border: 'var(--purple-tint-strong)' },
  panel: { bg: 'var(--blue-tint-medium)', color: 'var(--cyan-text-dark)', border: 'var(--blue-tint-strong)' },
};

function getBadgeColors(label: string | undefined) {
  if (!label) return null;
  const lower = label.toLowerCase();
  for (const [key, colors] of Object.entries(BADGE_COLORS)) {
    if (lower.includes(key)) return colors;
  }
  return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'var(--border-color)' };
}

const PageIndexSidebar: React.FC<PageIndexSidebarProps> = ({
  numPages,
  pageNumber,
  pageIndex,
  pdfDocument,
  onNavigateToPage,
  onReindex,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isReindexing, setIsReindexing] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<number, string>>({});
  const listRef = useRef<HTMLDivElement>(null);

  // Generate all thumbnails concurrently on PDF load
  useEffect(() => {
    if (!pdfDocument || !numPages) return;

    let cancelled = false;

    const generateAll = async () => {
      const BATCH = 6;
      const results: Record<number, string> = {};

      for (let start = 1; start <= numPages; start += BATCH) {
        const batch = Array.from(
          { length: Math.min(BATCH, numPages - start + 1) },
          (_, i) => start + i
        );

        const rendered = await Promise.all(
          batch.map(async (pg) => {
            try {
              const page = await pdfDocument.getPage(pg);
              const viewport = page.getViewport({ scale: 0.05 });
              const canvas = document.createElement('canvas');
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              const ctx = canvas.getContext('2d');
              if (!ctx) return { pg, url: '' };

              await page.render({ canvasContext: ctx, viewport }).promise;
              const url = canvas.toDataURL('image/jpeg', 0.5);
              canvas.width = 0;
              canvas.height = 0;
              return { pg, url };
            } catch {
              return { pg, url: '' };
            }
          })
        );

        if (cancelled) return;

        for (const { pg, url } of rendered) {
          if (url) results[pg] = url;
        }
        setThumbnails((prev) => ({ ...prev, ...results }));
      }
    };

    generateAll();
    return () => { cancelled = true; };
  }, [pdfDocument, numPages]);

  // Scroll active page into view when page changes
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.pis-item.active') as HTMLElement;
    if (active) {
      active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [pageNumber]);

  return (
    <div className={`pis-root${isOpen ? '' : ' collapsed'}`}>
      {/* Header with toggle */}
      <div className="pis-header">
        {isOpen && (
          <>
            <span className="pis-title">Pages</span>
            <span className="pis-count">{numPages || 0}</span>
          </>
        )}
        {isOpen && onReindex && (
          <button
            className="pis-toggle"
            onClick={async () => {
              setIsReindexing(true);
              try { await onReindex(); } finally { setIsReindexing(false); }
            }}
            disabled={isReindexing}
            title="Re-index pages"
            style={{ marginRight: 2 }}
          >
            <RefreshCw size={13} style={isReindexing ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>
        )}
        <button
          className="pis-toggle"
          onClick={() => setIsOpen((o) => !o)}
          title={isOpen ? 'Collapse pages' : 'Expand pages'}
        >
          {isOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
        </button>
      </div>

      {/* Page list */}
      <div className="pis-list" ref={listRef}>
        {numPages && numPages > 0 ? (
          Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => {
            const info = pageIndex?.find((p) => p.page === pg);
            const isCurrent = pg === pageNumber;
            const badgeColors = getBadgeColors(info?.label);

            return (
              <button
                key={pg}
                className={`pis-item${isCurrent ? ' active' : ''}`}
                onClick={() => onNavigateToPage(pg)}
                title={isOpen ? undefined : `Page ${pg}${info?.sheetName ? ` · ${info.sheetName}` : ''}`}
              >
                {/* Thumbnail — takes up most of the card */}
                <div className="pis-thumb-wrapper">
                  {thumbnails[pg] ? (
                    <img
                      src={thumbnails[pg]}
                      alt={`Page ${pg}`}
                      className="pis-thumb"
                      draggable={false}
                    />
                  ) : (
                    <div className="pis-thumb-placeholder" />
                  )}
                  <span className="pis-page-num">{pg}</span>
                  {isCurrent && <span className="pis-active-dot" />}
                </div>

                {/* Title + badge stacked below thumbnail */}
                <div className="pis-info">
                  <span className="pis-page-title">
                    {info?.sheetName || `Page ${pg}`}
                  </span>
                  {badgeColors && info?.label && (
                    <span
                      className="pis-badge"
                      style={{
                        color: badgeColors.color,
                        background: badgeColors.bg,
                        border: `1px solid ${badgeColors.border}`,
                      }}
                    >
                      {info.label}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        ) : (
          <div className="pis-empty">No pages</div>
        )}
      </div>
    </div>
  );
};

export default PageIndexSidebar;
